import React, { useEffect, useRef } from 'react'
import { DEMAND_STYLE } from '@/lib/market'
import { ABBREV_TOOLTIPS } from '@/lib/plain-language'
import { cn } from '@/lib/utils'
import { CHART_MARGIN_RIGHT, CHART_Y_AXIS_WIDTH } from '@/components/market/PositionChart'
import type { MarketNight, MarketTone } from '@/types/market'

/**
 * Four aligned lanes under the chart, one column per night: events you logged, competitors sold
 * out, market demand (height AND colour), and your own on-the-books occupancy (teal). Market over
 * OTB is the point: a tall orange bar over a short teal one reads "the market is filling and you
 * are not" without a number.
 *
 * It is also the keyboard path through the nights: a horizontal listbox where selection follows
 * focus. Left/Right one night, PageUp/PageDown a week, Home/End the ends.
 */

const LANES = [
  { key: 'event', label: 'Event', help: 'Events you have logged for that night.', height: 'h-2' },
  { key: 'soldOut', label: 'Sold out', help: 'Competitors with no room for two that night.', height: 'h-5' },
  {
    key: 'market',
    label: 'Market',
    help: 'How the compset median compares with a typical night for that weekday.',
    height: 'h-6',
  },
  { key: 'otb', label: 'OTB', help: ABBREV_TOOLTIPS.OTB, height: 'h-6' },
] as const

export function nightOptionLabel(night: MarketNight, worthALook: boolean): string {
  const parts = [night.label, `Market ${night.marketDemandLabel}`, `${night.competitorsSoldOut} sold out`]
  if (night.eventName) parts.push(night.eventName)
  if (night.occupancyLabel) parts.push(night.occupancyLabel)
  if (worthALook) parts.push('Worth a look')
  return `${parts.join('. ')}.`
}

/** Index after a navigation key, or null when the key is not one of ours. */
export function nextIndex(key: string, current: number, count: number): number | null {
  const clamp = (i: number) => (i < 0 ? 0 : i > count - 1 ? count - 1 : i)
  switch (key) {
    case 'ArrowRight':
      return clamp(current + 1)
    case 'ArrowLeft':
      return clamp(current - 1)
    case 'PageDown':
      return clamp(current + 7)
    case 'PageUp':
      return clamp(current - 7)
    case 'Home':
      return 0
    case 'End':
      return count - 1
    default:
      return null
  }
}

export default function NightLanes({
  nights,
  attention,
  selectedDate,
  hoverIndex,
  onHover,
  onSelect,
}: {
  nights: MarketNight[]
  attention: Map<string, MarketTone>
  selectedDate: string | null
  hoverIndex: number | null
  onHover: (index: number | null) => void
  onSelect: (date: string) => void
}) {
  const listRef = useRef<HTMLDivElement>(null)
  const optionRefs = useRef<(HTMLDivElement | null)[]>([])
  // With fewer than two competitors priced on every night there is no market to measure: say so
  // once in the lane rather than leaving it blank.
  const noMarket = nights.length > 0 && nights.every((n) => n.marketDemand === 'unknown')
  const selectedIndex = Math.max(
    0,
    nights.findIndex((n) => n.date === selectedDate)
  )

  // Selection follows focus: when the keyboard moved the selection, move focus with it.
  useEffect(() => {
    const list = listRef.current
    if (!list || !list.contains(document.activeElement)) return
    optionRefs.current[selectedIndex]?.focus({ preventScroll: true })
  }, [selectedIndex])

  const onKeyDown = (event: React.KeyboardEvent) => {
    const next = nextIndex(event.key, selectedIndex, nights.length)
    if (next == null) return
    event.preventDefault()
    onSelect(nights[next].date)
  }

  return (
    <div
      className="relative mt-1"
      style={{ paddingLeft: CHART_Y_AXIS_WIDTH, paddingRight: CHART_MARGIN_RIGHT }}
      onMouseLeave={() => onHover(null)}
    >
      {/* Gutter labels, aligned with the lane rows. */}
      <div
        className="pointer-events-none absolute left-0 top-0 flex flex-col gap-1.5 pt-1.5"
        style={{ width: CHART_Y_AXIS_WIDTH }}
      >
        {LANES.map((lane) => (
          <div key={lane.key} className={cn('flex items-center', lane.height)}>
            <span
              title={lane.help}
              className="pointer-events-auto cursor-help pr-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
            >
              {lane.label}
            </span>
          </div>
        ))}
      </div>

      {noMarket && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute z-[1] text-[11px] italic text-muted-foreground"
          style={{ left: CHART_Y_AXIS_WIDTH + 8, top: 52 }}
        >
          {nights[0].marketDemandLabel}
        </span>
      )}

      <div
        ref={listRef}
        role="listbox"
        aria-orientation="horizontal"
        aria-label="Nights"
        onKeyDown={onKeyDown}
        className="grid rounded-md"
        style={{ gridTemplateColumns: `repeat(${nights.length}, minmax(0, 1fr))` }}
      >
        {nights.map((night, i) => {
          const selected = night.date === selectedDate
          const hovered = i === hoverIndex
          const tone = attention.get(night.date)
          const demand = DEMAND_STYLE[night.marketDemand]
          const prevEvent = i > 0 && nights[i - 1].eventName != null
          const nextEvent = i < nights.length - 1 && nights[i + 1].eventName != null
          return (
            <div
              key={night.date}
              ref={(el) => {
                optionRefs.current[i] = el
              }}
              role="option"
              aria-selected={selected}
              aria-label={nightOptionLabel(night, tone != null)}
              tabIndex={i === selectedIndex ? 0 : -1}
              onClick={() => onSelect(night.date)}
              onMouseEnter={() => onHover(i)}
              className={cn(
                'relative flex cursor-pointer flex-col gap-1.5 py-1.5 outline-none transition-colors',
                'focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-primary/50',
                selected ? 'bg-primary/10' : hovered ? 'bg-slate-200/60' : night.isWeekend ? 'bg-slate-100' : ''
              )}
            >
              {/* Event: touching bars, so a three-night event reads as one bar. */}
              <div className="flex h-2 items-center" title={night.eventName ?? undefined}>
                {night.eventName && (
                  <span
                    className={cn(
                      'h-1 w-full bg-slate-900',
                      !prevEvent && 'ml-px rounded-l-full',
                      !nextEvent && 'mr-px rounded-r-full'
                    )}
                  />
                )}
              </div>

              {/* Sold out: one dot per competitor sold out, four at most before a plus. */}
              <div className="flex h-5 flex-col-reverse items-center gap-px overflow-hidden">
                {Array.from({ length: Math.min(night.competitorsSoldOut, 4) }).map((_, d) => (
                  <span key={d} className="h-1 w-1 shrink-0 rounded-full bg-slate-800" />
                ))}
                {night.competitorsSoldOut > 4 && (
                  <span className="text-[8px] font-bold leading-none text-slate-800">+</span>
                )}
              </div>

              {/* Market demand: height and colour. */}
              <div
                className="flex h-6 items-end px-[2px]"
                title={night.marketDemandSentence ?? night.marketDemandLabel}
              >
                <span className={cn('w-full rounded-t-sm', demand.bar)} style={{ height: demand.height }} />
              </div>

              {/* Your occupancy on the books. The height is a layout proportion, never printed. */}
              <div className="flex h-6 items-end px-[2px]" title={night.occupancyLabel ?? undefined}>
                {night.occupancyPct != null && (
                  <span
                    className="w-full rounded-t-sm bg-primary/60"
                    style={{ height: `${night.occupancyPct}%`, minHeight: night.occupancyPct > 0 ? 2 : 0 }}
                  />
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
